import codeSnippets from './codeSnippets.json';
import { EvgenEvent } from './IEvgen';

export interface TemplateVariables {
  [key: string]: string | number | boolean;
}

export const interpolateTemplate = (template: string, variables: TemplateVariables): string => {
  let result = template;
  
  Object.entries(variables).forEach(([key, value]) => {
    const placeholder = `{{${key}}}`;
    result = result.replace(new RegExp(placeholder, 'g'), String(value));
  });
  
  return result;
};

export const getCodeSnippet = (path: string): string => {
  const keys = path.split('.');
  let current: Record<string, unknown> | string = codeSnippets;
  
  for (const key of keys) {
    if (current && typeof current === 'object' && key in current) {
      current = current[key] as Record<string, unknown> | string;
    } else {
      console.warn(`Code snippet path not found: ${path}`);
      return '';
    }
  }
  
  return typeof current === 'string' ? current : '';
};

export const generateEventCode = (
  platform: 'android' | 'ios' | 'reactNative',
  language: 'java' | 'kotlin' | 'swift' | 'objc' | 'javascript',
  eventName: string,
  parameters: Array<{ name: string; type: 'predefined' | 'custom' }>,
  withListener: boolean = false
): { primary: string; secondary: string } => {
  const config = getPlatformConfig(platform, language);
  const template = getCodeSnippet(config.templatePath);
  
  const paramLines = parameters.map(param => {
    const paramTemplate = getCodeSnippet(config.paramLinePath);
    return interpolateTemplate(paramTemplate, {
      PARAM_NAME: param.name
    });
  }).join('\n');
  
  let code = interpolateTemplate(template, {
    EVENT_NAME: eventName,
    PARAM_LINES: paramLines
  });
  
  if (withListener) {
    const listenerCode = getCodeSnippet(config.listenerPath);
    code = code.replace(config.listenerInsertionPoint, listenerCode);
  }
  
  return {
    primary: code,
    secondary: language === 'kotlin' ? code : getCodeSnippet(config.secondaryTemplatePath)
  };
};

const getPlatformConfig = (platform: string, language: string) => {
  const configs = {
    android: {
      java: {
        templatePath: 'android.eventCode.java.template',
        paramLinePath: 'android.eventCode.java.paramLine',
        secondaryTemplatePath: 'android.eventCode.kotlin.template',
        listenerPath: 'android.withListener.java',
        listenerInsertionPoint: 'eventValues);'
      },
      kotlin: {
        templatePath: 'android.eventCode.kotlin.template',
        paramLinePath: 'android.eventCode.kotlin.paramLine',
        secondaryTemplatePath: 'android.eventCode.java.template',
        listenerPath: 'android.withListener.kotlin',
        listenerInsertionPoint: 'eventValues)'
      }
    },
    ios: {
      swift: {
        templatePath: 'ios.eventCode.swift.template',
        paramLinePath: 'ios.eventCode.swift.paramLine',
        secondaryTemplatePath: 'ios.eventCode.objc.template',
        listenerPath: 'ios.withListener.swift.completionHandler',
        listenerInsertionPoint: '  );'
      },
      objc: {
        templatePath: 'ios.eventCode.objc.template',
        paramLinePath: 'ios.eventCode.objc.paramLine',
        secondaryTemplatePath: 'ios.eventCode.swift.template',
        listenerPath: 'ios.withListener.objc.completionHandler',
        listenerInsertionPoint: '}];'
      }
    },
    reactNative: {
      javascript: {
        templatePath: 'reactNative.eventCode.template',
        paramLinePath: 'reactNative.eventCode.paramLine',
        secondaryTemplatePath: 'reactNative.eventCode.template',
        listenerPath: '',
        listenerInsertionPoint: ''
      }
    }
  };
  
  return configs[platform]?.[language] || configs.android.java;
};

export const generateEvgenCode = (
  platform: 'android' | 'ios' | 'reactNative',
  language: 'java' | 'kotlin' | 'swift' | 'objc' | 'javascript',
  events: EvgenEvent[]
): string => {
  const template = getCodeSnippet(`evgen.${platform}.${language}.template`);
  
  const eventCodes = events.map(event => {
    const paramLines = event.parameters.map(param => {
      const paramTemplate = getCodeSnippet(`evgen.${platform}.${language}.paramLine`);
      return interpolateTemplate(paramTemplate, {
        PARAM_NAME: param.parameterIdentifier
      });
    }).join('\n');
    
    return interpolateTemplate(template, {
      EVENT_IDENTIFIER: event.eventIdentifier,
      EVENT_NAME: event.eventType === "predefined" ? event.eventIdentifier.toUpperCase() : `"${event.eventIdentifier}"`,
      PARAM_LINES: paramLines
    });
  }).join('\n\n');
  
  return eventCodes;
};

export default {
  interpolateTemplate,
  getCodeSnippet,
  generateEventCode,
  generateEvgenCode
}; 